import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import UserService from "services/UserService";
import { MobileSvg } from "assets/icons/MobileSvg";
import {
  PLEASE_ENTER_VALID_CONFIRMATION_CODE,
  REGISTRATION_SUCCESSFUL,
  INVALID_CONFIRMATION_CODE,
  CONFIRMATION_FAILED,
  CONFIRMATION_CODE_RESENT,
  CONFIRMATION_CODE_RESEND_FAILED,
  PLEASE_TRY_AGAIN_LATER,
  PLEASE_LOGIN_WITH_CREDENTIALS,
  PLEASE_ENTER_SIX_DIGIT_CONFIRMATION_CODE_SENT_TO_MOBILE,
  LABEL_ENTER_CONFIRMATION_CODE,
} from "configurations/MessageConstants";
import { useNavigate } from "react-router-dom";

const ConfirmationCodePopup = ({ show, onClose, email }) => {
  const navigate = useNavigate();
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRef = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      confirmationCode: "",
    },
  });

  const confirmationCode = watch("confirmationCode");

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    let timer;
    if (resendCooldown <= 0) return;
    timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const onSubmit = async (data) => {
    try {
      const response = await UserService.confirmCode(
        email,
        data.confirmationCode
      );
      if (response.success) {
        toast.success(
          <div>
            {REGISTRATION_SUCCESSFUL}
            <br />
            {PLEASE_LOGIN_WITH_CREDENTIALS}
          </div>
        );
        handleConfirmationSuccess();
      } else {
        toast.error(INVALID_CONFIRMATION_CODE);
      }
    } catch (error) {
      console.error(CONFIRMATION_FAILED, error);
      toast.error(
        <div>
          {CONFIRMATION_FAILED}
          <br />
          {PLEASE_TRY_AGAIN_LATER}
        </div>
      );
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setIsResendLoading(true);
    try {
      await UserService.resendConfirmationCode(email);
      toast.success(CONFIRMATION_CODE_RESENT);
      setResendCooldown(60);
    } catch (error) {
      console.error(CONFIRMATION_CODE_RESEND_FAILED, error);
      toast.error(
        <div>
          {CONFIRMATION_CODE_RESEND_FAILED}
          <br />
          {PLEASE_TRY_AGAIN_LATER}
        </div>
      );
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setResendCooldown(0);
    onClose();
  };

  const handleConfirmationSuccess = () => {
    handleClose();
    navigate("/login", { replace: true });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="confirmation-modal"
      centered
      aria-labelledby="confirmation-code-popup"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="confirmation-code-popup">
          Confirm Your Mobile Number
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="confirmation-icon">
          <MobileSvg />
        </div>
        <div className="mb-2 text-muted">
          {PLEASE_ENTER_SIX_DIGIT_CONFIRMATION_CODE_SENT_TO_MOBILE}
        </div>

        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-4">
            <Controller
              name="confirmationCode"
              control={control}
              rules={{
                required: PLEASE_ENTER_VALID_CONFIRMATION_CODE,
                validate: (value) => {
                  if (!/^\d{6}$/.test(value)) {
                    return PLEASE_ENTER_VALID_CONFIRMATION_CODE;
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  ref={inputRef}
                  type="text"
                  placeholder={LABEL_ENTER_CONFIRMATION_CODE}
                  className="text-center input-underline letter-spacing-wide"
                  maxLength={6}
                  disabled={isSubmitting}
                  isInvalid={!!errors.confirmationCode}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    field.onChange(numericValue);
                  }}
                  style={{
                    caretColor:
                      field.value.length >= 6 ? "transparent" : "auto",
                  }}
                  autoComplete="off"
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmationCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Button
            variant="primary"
            className="w-100"
            type="submit"
            disabled={isSubmitting || confirmationCode.length !== 6}
            size="lg"
          >
            {isSubmitting ? (
              <>
                Confirming Code
                <Spinner animation="border" size="sm" className="ms-2" />
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Form>

        <div className="resend-section">
          <p className="mb-1">
            Didn't receive the code?{" "}
            {resendCooldown > 0 ? (
              <span className="countdown-timer">
                Resend available in {resendCooldown}s
              </span>
            ) : (
              <Button
                variant="link"
                onClick={handleResendCode}
                disabled={isSubmitting}
                className="p-0"
              >
                {isResendLoading ? (
                  <Spinner animation="border" size="sm" className="ms-2" />
                ) : (
                  "Resend Code"
                )}
              </Button>
            )}
          </p>
          {resendCooldown === 0 && (
            <small className="text-muted">
              Make sure to check your SMS messages before resending the code.
            </small>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ConfirmationCodePopup;
